import { useState, useContext } from "react";
import { useNavigate, Link } from "react-router";
import { toast } from 'react-hot-toast';
import GridShape from "../../components/common/GridShape";
import Input from "../../components/form/input/InputField";
import Label from "../../components/form/Label";
import { ChevronLeftIcon, EyeCloseIcon, EyeIcon } from "../../icons";
import Checkbox from "../../components/form/input/Checkbox";
import Button from "../../components/ui/button/Button";
import PageMeta from "../../components/common/PageMeta";
import AuthContext from "../../context/AuthContext";
import { authService } from "../../services/authService";

export default function SignIn() {
  const navigate = useNavigate();
  const { login: contextLogin } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
    showPassword: false
  });
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    const loadingToast = toast.loading('Iniciando sesión...');

    try {
      const token = await authService.login({
        email: formData.email,
        password: formData.password
      });

      await contextLogin(token, formData.rememberMe);
      toast.dismiss(loadingToast);

      navigate('/');
    } catch (error) {
      toast.dismiss(loadingToast);

      let errorMessage = 'Error desconocido al intentar iniciar sesión';
      if (error instanceof Error) {
        if (error.message.includes('Invalid credentials')) {
          errorMessage = 'Credenciales inválidas. Verifica tu correo y contraseña.';
        } else if (error.message.includes('Invalid password')) {
          errorMessage = 'Contraseña incorrecta. Por favor, intenta de nuevo.';
        } else if (error.message.includes('network error')) {
          errorMessage = 'Error de red. Por favor, verifica tu conexión a Internet.';
        } else if (error.message.includes('user not found')) {
          errorMessage = 'Usuario no encontrado. Por favor, verifica tu correo electrónico.';
        }
      }

      toast.error(errorMessage, {
        duration: 3000,
        icon: '❌',
      });
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <PageMeta
        title="Iniciar sesión | Destiny Caribbean Tours"
        description="Panel de administración de Destiny Caribbean Tours"
      />
      <div className="relative flex w-full h-screen px-4 py-6 overflow-hidden bg-white z-1 dark:bg-gray-900 sm:p-0">
        <div className="flex flex-col flex-1 p-6 rounded-2xl sm:rounded-none sm:border-0 sm:p-8">
          <div className="w-full max-w-md pt-10 mx-auto"></div>
          <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
            <div>
              <div className="mb-5 sm:mb-8">
                <h1 className="mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
                  Iniciar sesión
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  ¡Bienvenido al panel de administración de Destiny Caribbean Tours!
                </p>
              </div>
              <div>
                <form onSubmit={handleSubmit} method="POST">
                  <div className="space-y-6">
                    <div className="mb-5">
                      <Label>Correo electrónico</Label>
                      <Input
                        name="email"
                        type="email"
                        placeholder="Ingresa tu correo electrónico"
                        value={formData.email}
                        onChange={handleChange}
                        disabled={isLoading}
                      />
                    </div>
                    <div className="mb-5">
                      <Label>Contraseña</Label>
                      <div className="relative">
                        <Input
                          name="password"
                          type={formData.showPassword ? "text" : "password"}
                          placeholder="Ingresa tu contraseña"
                          value={formData.password}
                          onChange={handleChange}
                          disabled={isLoading}
                        />
                        <span
                          onClick={() => setFormData(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                          className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                        >
                          {formData.showPassword ? (
                            <EyeIcon className="fill-gray-500 dark:fill-gray-400" />
                          ) : (
                            <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400" />
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mb-5">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={formData.rememberMe}
                          onChange={(checked) => setFormData(prev => ({ ...prev, rememberMe: checked }))}
                          disabled={isLoading}
                        />
                        <span className="font-normal text-gray-700 text-theme-sm dark:text-gray-400">
                          Mantener sesión iniciada
                        </span>
                      </div>
                    </div>
                    <div>
                      <Button 
                        type="submit"
                        disabled={isLoading}
                        className="w-full"
                      >
                        {isLoading ? "Iniciando sesión..." : "Iniciar sesión"}
                      </Button>
                    </div>
                  </div>
                </form>
                <div className="mt-5">
                  <p className="text-sm font-normal text-center text-gray-700 dark:text-gray-400">
                    ¿No tienes una cuenta?{" "}
                    <Link
                      to="/register"
                      className="text-brand-500 hover:text-brand-600 dark:text-brand-400"
                    >
                      Registrarse
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="relative items-center justify-center flex-1 hidden p-8 z-1 bg-brand-950 dark:bg-white/5 lg:flex">
          <GridShape />
          <div className="flex flex-col items-center max-w-xs">
            <Link to="/" className="block mb-4">
              <img src="./images/logo/auth-logo.svg" alt="Destiny Caribbean Tours" />
            </Link>
            <p className="text-center text-gray-400 dark:text-white/60">
              Sistema de gestión de tours y reservas
            </p>
          </div>
        </div>
      </div>
    </>
  );
}